import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { observer } from 'mobx-react'
import { format } from 'date-fns'
import { isNil, snakeCase } from 'lodash'
import {
  Block,
  Button,
  ExternalLink,
  Heading,
  IconPdf,
  LinkList,
  LinkListItem,
  LoadingSpinner,
  Modal,
  ModalContent,
  ModalFooter,
  ModalTitle,
  Paragraph,
  Text,
} from 'suomifi-ui-components'
import { LanguageCode } from 'common/src/vtj/LanguageCode.enum'
import { Serialized } from 'common/src/vtj/types/util.type'
import { getSubjectPersons } from 'todistukset-common/src/vtj/todistus-person.util'
import {
  hasName,
  reversedDisplayNameOf,
} from 'todistukset-common/src/vtj/name.util'
import { TodistusStatus } from 'todistukset-common/src/vtj/types/todistus-status'
import { isPaymentFreeOfCharge } from 'todistukset-common/src/vtj/price.util'
import { isHetuIdentifiedUser } from 'todistukset-common/src/vtj/user.utils'
import { TodistusRatkaisuType } from 'todistukset-common/src/vtj/types/todistus-ratkaisu-type'
import {
  Payment,
  TodistusOrder,
  TodistusOrderOverview,
} from 'todistukset-common/src/vtj/types/todistus-order.type'
import {
  TodistusPyyntö,
  TodistusPyyntöAetOverview,
} from 'todistukset-common/src/vtj/types/todistus'
import { certificateTypeToTranslationKeyMap } from 'todistukset-common/src/vtj/certificate.util'
import LocalizedString, {
  LocalizedParagraphs,
} from 'todistukset-ui/src/vtj/components/localized-string/LocalizedString.react'
import { ContentBlock } from 'todistukset-ui/src/vtj/components/page-content/PageContent.sc'
import { useStore } from 'todistukset-ui/src/vtj/stores/store.util'
import publicSettings from 'todistukset-ui/src/vtj/public-settings'
import {
  ButtonContainer,
  CertificateMobile,
  CertificatesMobileList,
  ContentDivider,
  DesktopOrMobileContainer,
  FileList,
  MinimalButton,
  ModalOrderInfo,
  StyledTable,
} from 'todistukset-ui/src/vtj/components/my-certificates-page/MyCertificates.sc'
import { useNavigateToAetSignerForm } from 'todistukset-ui/src/vtj/components/order-wizard-aet-signer/aetSignerSteps'
import CertificateTypes from 'todistukset-common/src/vtj/CertificateTypes.enum'
import { useDocumentDownloadStatus } from 'todistukset-ui/src/vtj/hooks'
import { getVtjOteTodistusAsiakirjaName } from 'todistukset-common/src/vtj/asiakirja.util'
import { getIsAURA } from 'todistukset-common/src/vtj/form-check.util'
import { getIncludeOptionsFromTodistusPyynto } from 'todistukset-common/src/vtj/util/include-options.util'

type SerializedTodistusOrder = Serialized<TodistusOrder<TodistusPyyntö>>
type SerializedAetTodistusOverview = Serialized<TodistusPyyntöAetOverview>
type SerializedTodistusOrderOverview = Serialized<TodistusOrderOverview>

const isSpouseB = ({
  form,
  loggedInUserHetu,
}: {
  form: SerializedAetTodistusOverview
  loggedInUserHetu: string | null
}) =>
  loggedInUserHetu &&
  loggedInUserHetu.toLowerCase() === form.aetLisaosa.spouseBHetu.toLowerCase()

const isAetTodistusOverview = (
  form: SerializedTodistusOrderOverview['form']
): form is SerializedAetTodistusOverview =>
  form.todistusType === CertificateTypes.AETTODISTUS

const getOrderStatusTranslationKey = (
  order: SerializedTodistusOrderOverview,
  loggedInUserHetu: string | null
): `myCertificatesTableStatus_${string}` => {
  const { status, ratkaisuType } = order.form

  const getRatkaisuTypeTranslationKey = () => {
    switch (ratkaisuType) {
      case TodistusRatkaisuType.RATKAISTU:
      case TodistusRatkaisuType.AUTOMAATTISESTI_KASITELTY:
        return 'myCertificatesTableStatus_ratkaistu'
      case TodistusRatkaisuType.KIELTEINEN_PAATOS:
        return 'myCertificatesTableStatus_kielteinenPaatos'
      case TodistusRatkaisuType.SILLENSA_JATETTY:
        return 'myCertificatesTableStatus_silleensaJatetty'
      case TodistusRatkaisuType.RAUENNUT:
        return 'myCertificatesTableStatus_rauennut'
      case TodistusRatkaisuType.ASIAN_SIIRTO:
        return 'myCertificatesTableStatus_asianSiirto'
      case TodistusRatkaisuType.AIHEETON_VIENTI:
        return 'myCertificatesTableStatus_aiheetonVienti'
      case TodistusRatkaisuType.HYLATTY:
        return 'myCertificatesTableStatus_hylatty'
      case null:
        return 'myCertificatesTableStatus_unknown'
      default:
        const _exhaustiveCheck: never = ratkaisuType
        return 'myCertificatesTableStatus_unknown'
    }
  }

  const { todistusType } = order.form
  if (isAetTodistusOverview(order.form)) {
    switch (status) {
      case TodistusStatus.LUONNOS:
        return isSpouseB({ form: order.form, loggedInUserHetu })
          ? 'myCertificatesTableStatus_odottaaHyvaksymistasi'
          : 'myCertificatesTableStatus_odottaaPuolisonHyvaksymista'
      case TodistusStatus.ODOTTAA_TODISTUSTEN_LUONTIA:
      case TodistusStatus.ODOTTAA_ASIAN_LUONTIA:
      case TodistusStatus.ALOITTAMATTA:
        return 'myCertificatesTableStatus_vireilla'
      case TodistusStatus.KASITTELYSSA:
      case TodistusStatus.HARKINTA_AIKA:
        return 'myCertificatesTableStatus_kasittelyssa'
      case TodistusStatus.LUONNOS_HYLATTY:
        return 'myCertificatesTableStatus_puolisonHylkaama'
      case TodistusStatus.LUONNOS_VANHENTUNUT:
        return 'myCertificatesTableStatus_vanhentunut'
      case TodistusStatus.VALMIS:
        return getRatkaisuTypeTranslationKey()
      case TodistusStatus.ODOTTAA_LOPPUTOIMIA: {
        if (isAetOrderDownloadable({ order })) {
          return getRatkaisuTypeTranslationKey()
        }
        return 'myCertificatesTableStatus_kasittelyssa'
      }
      case TodistusStatus.ODOTTAA_MAKSUA:
      case null:
        return 'myCertificatesTableStatus_unknown'
      default:
        const _exhaustiveCheck: never = status
        return 'myCertificatesTableStatus_unknown'
    }
  } else if (todistusType === CertificateTypes.UAETTODISTUS) {
    switch (status) {
      case TodistusStatus.ODOTTAA_TODISTUSTEN_LUONTIA:
      case TodistusStatus.ODOTTAA_ASIAN_LUONTIA:
      case TodistusStatus.ALOITTAMATTA:
        return 'myCertificatesTableStatus_vireilla'
      case TodistusStatus.KASITTELYSSA:
        return 'myCertificatesTableStatus_kasittelyssa'
      case TodistusStatus.ODOTTAA_LOPPUTOIMIA:
      case TodistusStatus.VALMIS:
        return getRatkaisuTypeTranslationKey()
      case TodistusStatus.HARKINTA_AIKA:
      case TodistusStatus.LUONNOS:
      case TodistusStatus.LUONNOS_HYLATTY:
      case TodistusStatus.LUONNOS_VANHENTUNUT:
      case TodistusStatus.ODOTTAA_MAKSUA:
      case null:
        return 'myCertificatesTableStatus_unknown'
      default:
        const _exhaustiveCheck: never = status
        return 'myCertificatesTableStatus_unknown'
    }
  }

  return 'myCertificatesTableStatus_unknown'
}

const isAetOrderApprovable = ({
  order,
  loggedInUserHetu,
}: {
  order: SerializedTodistusOrderOverview
  loggedInUserHetu: string | null
}) =>
  isAetTodistusOverview(order.form) &&
  order.form.status === TodistusStatus.LUONNOS &&
  loggedInUserHetu &&
  loggedInUserHetu.toLowerCase() ===
    order.form.aetLisaosa.spouseBHetu.toLowerCase()

const isAetOrderDownloadable = ({
  order,
}: {
  order: SerializedTodistusOrderOverview
}) =>
  isAetTodistusOverview(order.form) &&
  order.form.ratkaisuType === TodistusRatkaisuType.RATKAISTU &&
  !isNil(order.form.asiakirjojenItsepalveluLuovutuspvm) &&
  order.form.status &&
  [TodistusStatus.ODOTTAA_LOPPUTOIMIA, TodistusStatus.VALMIS].includes(
    order.form.status
  )

const getAetDownloadUrl = ({
  order,
  t,
}: {
  order: SerializedTodistusOrderOverview
  t: TFunction
}) =>
  `/api/restricted/todistukset/aet-download/${order.form.requestId}/${snakeCase(
    t('aettodistus')
  )}.pdf`

const AetCertificatesTable = observer(
  ({
    orders,
    lang,
    loggedInUserHetu,
  }: {
    orders: SerializedTodistusOrderOverview[]
    lang: LanguageCode
    loggedInUserHetu: string | null
  }) => {
    const navigateToAetSignerForm = useNavigateToAetSignerForm()
    const { t } = useTranslation()
    return (
      <StyledTable data-test-id="ordered-aet-certificates-table">
        <thead>
          <tr>
            <th scope="col" align="left">
              <LocalizedString stringKey="myCertificatesTableHeadingCertificateType" />
            </th>
            <th scope="col" align="left">
              <LocalizedString stringKey="myCertificatesTableHeadingCertificateStatus" />
            </th>
            <th scope="col" align="right">
              <LocalizedString stringKey="myCertificatesTableHeadingOrderedAt" />
            </th>
            <th scope="col" align="left"></th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => (
            <tr
              key={order.form.requestId}
              data-test-id={`aet-todistus-for-request-${order.form.requestId}`}
            >
              <th scope="row" align="left">
                <LocalizedString
                  stringKey={
                    certificateTypeToTranslationKeyMap[order.form.todistusType]
                  }
                />
              </th>
              <td
                align="left"
                data-test-id={`ordered-aet-certificates-table-status-for-request-${order.form.requestId}`}
              >
                <LocalizedString
                  stringKey={getOrderStatusTranslationKey(
                    order,
                    loggedInUserHetu
                  )}
                />
              </td>
              <td align="right">
                {format(new Date(order.form.createdAt), 'd.M.yyyy, H:mm')}
              </td>
              <td align="left">
                <ButtonContainer>
                  {isAetOrderApprovable({
                    order,
                    loggedInUserHetu,
                  }) && (
                    <MinimalButton
                      variant="secondaryNoBorder"
                      onClick={() =>
                        navigateToAetSignerForm(order.form.requestId, lang)
                      }
                    >
                      <LocalizedString stringKey="myCertificatesTableApproveOrDeclineCertificateRequest" />
                    </MinimalButton>
                  )}
                  {isAetOrderDownloadable({ order }) && (
                    <div>
                      <IconPdf />
                      <ExternalLink
                        href={getAetDownloadUrl({ order, t })}
                        labelNewWindow={t('opensInNewWindow')}
                        aria-label={t('myCertificatesTableOpenCertificate')}
                        toNewWindow
                        smallScreen
                      >
                        <LocalizedString stringKey="myCertificatesTableOpenCertificate" />
                      </ExternalLink>
                    </div>
                  )}
                  {hasReceipt(order) && (
                    <DownloadReceipt payment={order.payment} />
                  )}
                </ButtonContainer>
              </td>
            </tr>
          ))}
        </tbody>
      </StyledTable>
    )
  }
)

const AetCertificatesMobileList = observer(
  ({
    orders,
    lang,
    loggedInUserHetu,
  }: {
    orders: SerializedTodistusOrderOverview[]
    lang: LanguageCode
    loggedInUserHetu: string | null
  }) => {
    const navigateToAetSignerForm = useNavigateToAetSignerForm()
    const { t } = useTranslation()
    return (
      <CertificatesMobileList>
        {orders.map((order) => (
          <CertificateMobile key={order.form.requestId}>
            <Block>
              <Heading variant="h5" smallScreen>
                <LocalizedString stringKey="myCertificatesTableHeadingCertificateType" />
              </Heading>
              <Text smallScreen>
                <LocalizedString
                  stringKey={
                    certificateTypeToTranslationKeyMap[order.form.todistusType]
                  }
                />
              </Text>
            </Block>

            <Block mt="s">
              <Heading variant="h5" smallScreen>
                <LocalizedString stringKey="myCertificatesTableHeadingCertificateStatus" />
              </Heading>
              <Text smallScreen>
                <LocalizedString
                  stringKey={getOrderStatusTranslationKey(
                    order,
                    loggedInUserHetu
                  )}
                />
              </Text>
            </Block>

            <Block mt="s">
              <Heading variant="h5" smallScreen>
                <LocalizedString stringKey="myCertificatesTableHeadingOrderedAt" />
              </Heading>
              <Text smallScreen>
                {format(new Date(order.form.createdAt), 'd.M.yyyy, H:mm')}
              </Text>
            </Block>

            <Block mt="s">
              <ButtonContainer>
                {isAetOrderApprovable({
                  order,
                  loggedInUserHetu,
                }) && (
                  <MinimalButton
                    variant="secondaryNoBorder"
                    onClick={() =>
                      navigateToAetSignerForm(order.form.requestId, lang)
                    }
                  >
                    <LocalizedString stringKey="myCertificatesTableApproveOrDeclineCertificateRequest" />
                  </MinimalButton>
                )}
                {isAetOrderDownloadable({ order }) && (
                  <div>
                    <IconPdf />
                    <ExternalLink
                      href={getAetDownloadUrl({ order, t })}
                      labelNewWindow={t('opensInNewWindow')}
                      aria-label={t('myCertificatesTableOpenCertificate')}
                      toNewWindow
                      smallScreen
                    >
                      <LocalizedString stringKey="myCertificatesTableOpenCertificate" />
                    </ExternalLink>
                  </div>
                )}
                {hasReceipt(order) && (
                  <DownloadReceipt payment={order.payment} />
                )}
              </ButtonContainer>
            </Block>
          </CertificateMobile>
        ))}
      </CertificatesMobileList>
    )
  }
)

const OrderedCertificatesTable = observer(
  ({
    setCertificateModalOrder,
    orders,
  }: {
    setCertificateModalOrder: (order: SerializedTodistusOrder) => void
    orders: SerializedTodistusOrder[]
  }) => {
    return (
      <StyledTable data-test-id="ordered-certificates-table">
        <thead>
          <tr>
            <th scope="col" align="left">
              <LocalizedString stringKey="myCertificatesTableHeadingCertificateType" />
            </th>
            <th scope="col" align="left">
              <LocalizedString stringKey="myCertificatesTableHeadingSubject" />
            </th>
            <th scope="col" align="right">
              <LocalizedString stringKey="myCertificatesTableHeadingOrderedAt" />
            </th>
            <th scope="col" align="left">
              <LocalizedString stringKey="myCertificatesTableHeadingFiles" />
            </th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => {
            const subjects = getSubjectPersons(order.form.persons)
            const subject = subjects[0]
            const additionalSubjectCount = subjects.length - 1
            return (
              <tr
                key={order.form.requestId}
                data-test-id={`todistus-for-payment-${order.payment?.paymentId}`}
              >
                <th scope="row" align="left">
                  <LocalizedString
                    stringKey={
                      certificateTypeToTranslationKeyMap[
                        order.form.todistusType
                      ]
                    }
                  />
                </th>
                <td align="left">
                  {hasName(subject) ? (
                    reversedDisplayNameOf(subject)
                  ) : (
                    <LocalizedString stringKey="dependantNameNotAvailable" />
                  )}
                  {additionalSubjectCount > 0 && (
                    <>
                      {' '}
                      <LocalizedString
                        stringKey="myCertificatesTableAdditionalSubjects"
                        values={{ count: additionalSubjectCount }}
                      />
                    </>
                  )}
                </td>
                <td align="right">
                  {format(new Date(order.form.createdAt), 'd.M.yyyy, H:mm')}
                </td>
                <td align="left">
                  <MinimalButton
                    onClick={() => setCertificateModalOrder(order)}
                    variant="secondaryNoBorder"
                  >
                    <LocalizedString stringKey="myCertificatesTableOpenModal" />
                  </MinimalButton>
                </td>
              </tr>
            )
          })}
        </tbody>
      </StyledTable>
    )
  }
)

const OrderedCertificatesMobileList = observer(
  ({
    setCertificateModalOrder,
    orders,
  }: {
    setCertificateModalOrder: (order: SerializedTodistusOrder) => void
    orders: SerializedTodistusOrder[]
  }) => {
    return (
      <CertificatesMobileList>
        {orders.map((order) => {
          const subjects = getSubjectPersons(order.form.persons)
          const subject = subjects[0]
          const additionalSubjectCount = subjects.length - 1
          return (
            <CertificateMobile
              key={order.form.requestId}
              data-test-id={`mobile-todistus-for-payment-${order.payment?.paymentId}`}
            >
              <Block>
                <Heading variant="h5" smallScreen>
                  <LocalizedString stringKey="myCertificatesTableHeadingCertificateType" />
                </Heading>
                <Text smallScreen>
                  <LocalizedString
                    stringKey={
                      certificateTypeToTranslationKeyMap[
                        order.form.todistusType
                      ]
                    }
                  />
                </Text>
              </Block>

              <Block mt="s">
                <Heading variant="h5" smallScreen>
                  <LocalizedString stringKey="myCertificatesTableHeadingSubject" />
                </Heading>
                <Text smallScreen>
                  {hasName(subject) ? (
                    reversedDisplayNameOf(subject)
                  ) : (
                    <LocalizedString stringKey="dependantNameNotAvailable" />
                  )}
                  {additionalSubjectCount > 0 && (
                    <>
                      {' '}
                      <LocalizedString
                        stringKey="myCertificatesTableAdditionalSubjects"
                        values={{ count: additionalSubjectCount }}
                      />
                    </>
                  )}
                </Text>
              </Block>

              <Block mt="s">
                <Heading variant="h5" smallScreen>
                  <LocalizedString stringKey="myCertificatesTableHeadingOrderedAt" />
                </Heading>
                <Text smallScreen>
                  {format(new Date(order.form.createdAt), 'd.M.yyyy, H:mm')}
                </Text>
              </Block>

              <Block mt="s">
                <Heading variant="h5" smallScreen>
                  <LocalizedString stringKey="myCertificatesTableHeadingFiles" />
                </Heading>
                <MinimalButton
                  onClick={() => setCertificateModalOrder(order)}
                  variant="secondaryNoBorder"
                >
                  <LocalizedString stringKey="myCertificatesTableOpenModal" />
                </MinimalButton>
              </Block>
            </CertificateMobile>
          )
        })}
      </CertificatesMobileList>
    )
  }
)

const OrderedCertificateModal = observer(
  ({
    order,
    onClose,
  }: {
    order: SerializedTodistusOrder | null
    onClose: () => void
  }) => {
    if (!order) {
      return
    }

    const isElectronic = getIsAURA(order.form)

    const paymentId = order.payment?.paymentId

    const {
      includeHetu,
      includeMaritalStatus,
      includePreviousPermanentAddresses,
      includePreviousMarriages,
      includePreviousNames,
      includeSpousePreviousNames,
    } = getIncludeOptionsFromTodistusPyynto(order.form)

    const selectedExtraInfoTranslationKeys: string[] = (
      [
        [
          'myCertificatesDownloadModalCertificateExtraInfo_includeHetu',
          includeHetu,
        ],
        [
          'myCertificatesDownloadModalCertificateExtraInfo_includeMaritalStatus',
          includeMaritalStatus,
        ],

        // Covers the following fields in the data model:
        // - includePreviousPermanentAddresses
        // - includePreviousTemporaryAddresses
        // - includePreviousKotikunta
        [
          'myCertificatesDownloadModalCertificateExtraInfo_includePreviousAddresses',
          includePreviousPermanentAddresses,
        ],
        [
          'myCertificatesDownloadModalCertificateExtraInfo_includePreviousMarriages',
          includePreviousMarriages,
        ],
        [
          'myCertificatesDownloadModalCertificateExtraInfo_includePreviousNames',
          includePreviousNames,
        ],
        [
          'myCertificatesDownloadModalCertificateExtraInfo_includeSpousePreviousNames',
          includeSpousePreviousNames,
        ],
      ] as [string, Serialized<boolean>][]
    )
      .filter(([_translationKey, status]) => status)
      .map(([translationKey]) => translationKey)

    return (
      <Modal appElementId="app" visible={!!order} onEscKeyDown={onClose}>
        <ModalContent data-test-id="certificate-files-modal-content">
          <ModalTitle>
            <LocalizedString stringKey="myCertificatesDownloadModalTitle" />
          </ModalTitle>
          <Paragraph>
            <LocalizedString stringKey="myCertificatesDownloadModalInfo" />
          </Paragraph>

          <ModalOrderInfo mt="m">
            <Block>
              <Heading variant="h5" smallScreen>
                <LocalizedString stringKey="myCertificatesDownloadModalOrdernumber" />
              </Heading>
              <Text smallScreen>{paymentId}</Text>
            </Block>
            <Block>
              <Heading variant="h5" smallScreen>
                <LocalizedString stringKey="myCertificatesDownloadModalOrder" />
              </Heading>
              <Text smallScreen>
                <LocalizedString
                  stringKey={
                    certificateTypeToTranslationKeyMap[order.form.todistusType]
                  }
                />
                {', '}
                <LocalizedString
                  stringKey="myCertificatesDownloadModalOrderedAt"
                  values={{
                    orderDate: format(
                      new Date(order.form.createdAt),
                      'd.M.yyyy'
                    ),
                  }}
                />
              </Text>
            </Block>
          </ModalOrderInfo>

          {selectedExtraInfoTranslationKeys.length > 0 && (
            <ModalOrderInfo mt="m">
              <Block>
                <Heading variant="h5" smallScreen>
                  <LocalizedString stringKey="myCertificatesDownloadModalCertificateExtraInfo" />
                </Heading>
                <Text
                  smallScreen
                  data-test-id="certificate-selected-extra-infos"
                >
                  {selectedExtraInfoTranslationKeys
                    .map<React.ReactNode>((key) => (
                      <LocalizedString stringKey={key} key={key} />
                    ))
                    .reduce((prev, curr) => [prev, ', ', curr])}
                </Text>
              </Block>
            </ModalOrderInfo>
          )}

          <Block mt="m">
            <FileList>
              {isElectronic && <TodistusDownloadLinks order={order} />}
              {hasReceipt(order) && (
                <li>
                  <DownloadReceipt payment={order.payment} />
                </li>
              )}
            </FileList>
          </Block>
        </ModalContent>
        <ModalFooter data-test-id="certificate-files-modal-footer">
          <Button onClick={onClose}>
            <LocalizedString stringKey="close" />
          </Button>
        </ModalFooter>
      </Modal>
    )
  }
)

const TodistusDownloadLinks = observer(
  ({ order }: { order: Serialized<TodistusOrder> }) => {
    const { t } = useTranslation()
    const { isDownloadable, isPolling, pollingTimedOut } =
      useDocumentDownloadStatus(order.form.requestId)

    const subjects = getSubjectPersons(order.form.persons)

    return (
      <>
        {(isPolling || pollingTimedOut) && (
          <LoadingSpinner
            text={
              pollingTimedOut
                ? t('documentCreationTimedOut')
                : t('documentsBeingCreated')
            }
            status={pollingTimedOut ? 'failed' : 'loading'}
            textAlign="right"
            variant="small"
            data-test-id={'download-certificate-loading-spinner'}
          />
        )}

        {isDownloadable &&
          subjects.map((subject) => {
            const personDisplayName = hasName(subject)
              ? reversedDisplayNameOf(subject)
              : t('dependantNameNotAvailable')

            const filename = getVtjOteTodistusAsiakirjaName({
              person: subject,
              todistusLanguage: order.form.todistusLanguage,
              todistusType: order.form.todistusType,
            })

            return (
              <li key={subject.subjectId}>
                <>
                  <IconPdf />
                  <ExternalLink
                    href={`/api/restricted/todistukset/vtj-download/${
                      order.form.requestId
                    }/${subject.personId}?filename=${encodeURIComponent(
                      filename
                    )}`}
                    underline="hover"
                    labelNewWindow={t('opensInNewWindow')}
                    aria-label={t(
                      'myCertificatesDownloadCertificateAriaLabel',
                      {
                        person: personDisplayName,
                      }
                    )}
                    toNewWindow
                    smallScreen
                    data-test-id={`download-certificate-${subject.subjectId}`}
                  >
                    {filename}
                  </ExternalLink>
                </>
              </li>
            )
          })}
      </>
    )
  }
)

const hasReceipt = (
  order: SerializedTodistusOrderOverview
): order is SerializedTodistusOrderOverview & { payment: Payment } =>
  !!order.payment &&
  !isPaymentFreeOfCharge(order.payment) &&
  order.payment.isPaid

const DownloadReceipt = ({ payment }: { payment: Payment }) => {
  const { t } = useTranslation()
  const { paymentId } = payment
  return (
    <div>
      <IconPdf />
      <ExternalLink
        href={`/api/restricted/todistukset/order-receipt/${paymentId}`}
        underline="hover"
        labelNewWindow={t('opensInNewWindow')}
        aria-label={t('myCertificatesDownloadReceiptAriaLabel', {
          paymentId,
        })}
        toNewWindow
        smallScreen
      >
        <LocalizedString stringKey="myCertificatesDownloadModalReceipt" />
      </ExternalLink>
    </div>
  )
}

const MyCertificates = observer((): React.JSX.Element => {
  const {
    previousOrders,
    aetStore,
    uaetOrders,
    common: { lang, loggedInUser },
  } = useStore()
  const settings = publicSettings.getPublicSettings()
  const { t } = useTranslation()

  const aetOrders = aetStore.aetOrders.map((form) => {
    return {
      form,
      payment: null,
    }
  })
  const aetAndUaetOrders = [...aetOrders, ...uaetOrders]

  const [certificateModalOrder, setCertificateModalOrder] =
    useState<SerializedTodistusOrder | null>(null)

  const user = loggedInUser.get()
  const loggedInUserHetu = isHetuIdentifiedUser(user) ? user.hetu : null

  return (
    <ContentBlock
      variant="section"
      padding="xl"
      pr="xxl"
      data-test-id="my-certificates-page-container"
    >
      <Heading variant="h2">
        <LocalizedString stringKey="myCertificatesSubTitle" />
      </Heading>
      <LocalizedParagraphs stringKey="myCertificatesIntro_paragraphs" />
      <ContentDivider />

      <Heading variant="h3">
        <LocalizedString stringKey="myCertificatesPendingTitle" />
      </Heading>
      <Text>
        <LocalizedString stringKey="myCertificatesPendingInfo" />
      </Text>
      <DesktopOrMobileContainer>
        {aetStore.isLoading && (
          <LoadingSpinner
            status={aetStore.error ? 'failed' : 'loading'}
            text={t('aetLoadingOrders')}
            textAlign="bottom"
            variant="small"
          />
        )}
        <AetCertificatesTable
          orders={aetAndUaetOrders}
          lang={lang}
          loggedInUserHetu={loggedInUserHetu}
        />
        <AetCertificatesMobileList
          orders={aetAndUaetOrders}
          lang={lang}
          loggedInUserHetu={loggedInUserHetu}
        />
      </DesktopOrMobileContainer>
      <ContentDivider />

      <Heading variant="h3">
        <LocalizedString stringKey="myCertificatesDownloadTitle" />
      </Heading>
      <LocalizedParagraphs
        stringKey={'myCertificatesDownloadInfo_paragraphs'}
      />
      <DesktopOrMobileContainer>
        <OrderedCertificatesTable
          setCertificateModalOrder={setCertificateModalOrder}
          orders={previousOrders}
        />
        <OrderedCertificatesMobileList
          setCertificateModalOrder={setCertificateModalOrder}
          orders={previousOrders}
        />
      </DesktopOrMobileContainer>

      <OrderedCertificateModal
        order={certificateModalOrder}
        onClose={() => setCertificateModalOrder(null)}
      />
      <ContentDivider />

      <Heading variant="h3" id="feedbackHeading">
        <LocalizedString stringKey="myCertificatesFeedbackTitle" />
      </Heading>
      <Text>
        <LocalizedString stringKey="myCertificatesFeedbackInfo" />
      </Text>

      <LinkList ariaLabelledBy="feedbackHeading">
        <LinkListItem>
          <ExternalLink
            labelNewWindow={t('opensInNewWindow')}
            href={settings.get('feedbackFormUrl')}
          >
            <LocalizedString stringKey="myCertificatesFeedbackAnonymousLinkName" />
          </ExternalLink>
        </LinkListItem>
      </LinkList>
    </ContentBlock>
  )
})

export default MyCertificates
